import { LoadingSpinner } from '@web_opxp/components';
import { LanguageService, SentryService, isLocalhost, isProduction } from '@web_opxp/features';
import { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { Route, RouterProvider, Routes, createBrowserRouter } from 'react-router';
import { HistoryService } from 'web_core_library';
import * as ENV from './base/env';
import { AssetsBaseUrl, BasePath, BuildID } from './base/env';
import initHreflangService from './base/hreflangService';
import NotAuthorizedPage from './error/pages/NotAuthorizedPage';
import NotFoundPage from './error/pages/NotFoundPage';
import GalleryApp from './gallery/GalleryApp';
import './index.scss';
import WelcomePage from './prereg/WelcomePage';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

if (!isLocalhost) {
  SentryService.initSentry(isProduction(), BuildID);
}

const OnboardingApp = lazy(() => import('./RootApp'));
const ExercisesBigLandingPage = lazy(() => import('./landing/exercisesBig'));
const ExercisesDeviceLandingPage = lazy(() => import('./landing/exercisesDevice'));
const QuestionnairePage = lazy(() => import('./questionnaire'));
const NativeLandingPage = lazy(() => import('./landing/native'));
const TestimonialLandingPage = lazy(() => import('./landing/testimonial'));
const GoLandingPage = lazy(() => import('./landing/lumosity'));
const QuizPage = lazy(() => import('./quiz'));
const CorpLandingPage = lazy(() => import('./landing/corp'));
const BlogGoLandingPage = lazy(() => import('./landing/blogGo'));
const AccountDeletedPage = lazy(() => import('./landing/accountDeleted'));
const AssessmentPage = lazy(() => import('./assessment/AssessmentPage'));
const EvaluationPage = lazy(() => import('./evaluation/EvaluationPage'));
const PrescriptionServicePage = lazy(() => import('./prescriptionService'));
const LegalsPage = lazy(() => import('./legals'));

const container = document.getElementById('root');
// eslint-disable-next-line
const root = createRoot(container!);

export const router = createBrowserRouter(
  [
    {
      path: '/*',
      element: (
        <Suspense fallback={<LoadingSpinner assetsUrl={AssetsBaseUrl} />}>
          <Routes>
            <Route path="/account-deleted" Component={AccountDeletedPage} />
            <Route path="/lp/exercises-big" Component={ExercisesBigLandingPage} />
            <Route path="/lp/frontpage/exercises-big" Component={() => <ExercisesBigLandingPage frontpage={true} />} />
            <Route path="/lp/exercises-big-white" Component={() => <ExercisesBigLandingPage color="white" />} />
            <Route
              path="/lp/frontpage/exercises-big-white"
              Component={() => <ExercisesBigLandingPage color="white" frontpage={true} />}
            />
            <Route path="/lp/exercises-device" Component={ExercisesDeviceLandingPage} />
            <Route
              path="/lp/frontpage/exercises-device"
              Component={() => <ExercisesDeviceLandingPage frontpage={true} />}
            />
            <Route path="/lp/exercises-device-white" Component={() => <ExercisesDeviceLandingPage color="white" />} />
            <Route
              path="/lp/frontpage/exercises-device-white"
              Component={() => <ExercisesDeviceLandingPage color="white" frontpage={true} />}
            />
            <Route path="/questionnaire" Component={QuestionnairePage} />
            <Route path="/lp/native" Component={NativeLandingPage} />
            <Route path="/lp/videofeedback/andrean" Component={TestimonialLandingPage} />
            <Route path="/lp/videoandrean" Component={() => <TestimonialLandingPage onboardingCTA />} />
            <Route path="/lp/go/*" Component={GoLandingPage} />
            <Route path="/lp/corp/*" Component={CorpLandingPage} />
            <Route path="/quiz" Component={QuizPage} />
            <Route path="/lp/blog_go/:locaId?/*" Component={BlogGoLandingPage} />
            <Route path="/prescriptionService" Component={PrescriptionServicePage} />
            <Route path="/med/prescriptionService" Component={PrescriptionServicePage} />
            <Route path="/legals/:type" Component={LegalsPage} />
            <Route path="/gallery/*" Component={GalleryApp} />
            <Route path="/*" Component={OnboardingApp}>
              <Route element={<WelcomePage />} index />
              <Route path="assessment/*" element={<AssessmentPage />} index />
              <Route path="evaluation/*" element={<EvaluationPage />} index />
              <Route path="welcome/*" element={<WelcomePage />} index />
              <Route path="not-authorized" Component={NotAuthorizedPage} />
              <Route path="*" Component={NotFoundPage} />
            </Route>
          </Routes>
        </Suspense>
      ),
    },
  ],
  { basename: BasePath }
);

HistoryService.initialize(router, {
  loginBaseUrl: ENV.LoginBaseUrl,
  logoutBaseUrl: ENV.LogoutBaseUrl,
  BlogBaseUrl: ENV.BlogBaseUrl,
  getLanguageFunction: () => LanguageService.getLanguage() as string,
  OnboardingAppPath: '/',
  SocialAppPath: ENV.SocialAppPath,
  SubscribeUrl: ENV.SubscribeBaseUrl,
  TrainingBaseUrl: ENV.TrainingBaseUrl,
  signupBaseUrl: ENV.RegistrationBaseUrl,
});

root.render(<RouterProvider router={router} />);

// init listener for hreflang tags udpate
initHreflangService();
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorkerRegistration.register();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
